import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FeatherChevronDown } from "@subframe/core";
import { SidebarWithSections } from "../../../ui/components/SidebarWithSections";
import { useAuth } from "../../../features/auth";
import {
  NAV_FOOTER_ITEM,
  NAV_SECTIONS,
  isNavItemActive,
  isNavItemVisible,
  type NavItemConfig,
} from "../config/navigation";

/**
 * SidebarV1
 *
 * The v1 left navigation. Renders the tree declared in `config/navigation.ts`
 * on top of the shared `SidebarWithSections` design-system component, so it
 * inherits the same hover, active and spacing treatment as the rest of the app.
 *
 * Badge counts are intentionally not rendered yet — they land once the other
 * sections migrate into this shell and we know what each one should count.
 */
export const SidebarV1: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { company, isClientView, isAdmin } = useAuth();

  const clientView = isClientView();
  const admin = isAdmin();

  const handleNavigate = (item: NavItemConfig): void => {
    if (item.disabled || !item.to) {
      return;
    }

    navigate(item.to);
  };

  const renderNavItem = (item: NavItemConfig): React.ReactNode => {
    const Icon = item.icon;
    const active = isNavItemActive(item, location.pathname);

    return (
      <SidebarWithSections.NavItem
        key={item.id}
        icon={<Icon />}
        selected={active}
        onClick={() => handleNavigate(item)}
        className={item.disabled ? "cursor-default opacity-50" : undefined}
        aria-current={active ? "page" : undefined}
        aria-disabled={item.disabled || undefined}
      >
        {item.label}
      </SidebarWithSections.NavItem>
    );
  };

  return (
    <SidebarWithSections
      className="shrink-0"
      header={
        <>
          <div className="flex items-center gap-2 pb-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-600">
              <span className="text-body-bold font-body-bold text-white">Z</span>
            </div>
            <span className="text-heading-3 font-heading-3 text-default-font">
              Zournal
            </span>
          </div>

          {/*
            Company display, not a switcher. The app is one company per tenant
            today; a real switcher needs multi-client scoping on every endpoint.
          */}
          <div className="flex w-full items-center gap-2 rounded-md border border-solid border-neutral-border px-3 py-2">
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-neutral-100">
              <span className="text-caption-bold font-caption-bold text-subtext-color">
                {(company?.companyName || "?").charAt(0).toUpperCase()}
              </span>
            </div>
            <span className="line-clamp-1 grow shrink-0 basis-0 text-body-bold font-body-bold text-default-font">
              {company?.companyName || "No company"}
            </span>
            <FeatherChevronDown className="text-body font-body text-subtext-color" />
          </div>
        </>
      }
      footer={
        isNavItemVisible(NAV_FOOTER_ITEM, clientView, admin)
          ? renderNavItem(NAV_FOOTER_ITEM)
          : null
      }
    >
      {NAV_SECTIONS.map((section) => {
        const visibleItems = section.items.filter((item) =>
          isNavItemVisible(item, clientView, admin)
        );

        if (visibleItems.length === 0) {
          return null;
        }

        return (
          <SidebarWithSections.NavSection
            key={section.id}
            label={section.label}
            className={section.label ? undefined : "pt-0"}
          >
            {visibleItems.map(renderNavItem)}
          </SidebarWithSections.NavSection>
        );
      })}
    </SidebarWithSections>
  );
};
